import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(10, 18, 33, 0.985);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
`;

const ImageFrame = styled(motion.div)`
  position: relative;
  width: min(95vw, 1600px);
  height: min(90vh, 1100px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 40px 80px rgba(0, 0, 0, 0.5);
  }
`;

const CloseBtn = styled(motion.button)`
  position: fixed;
  top: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: background 0.2s;
  z-index: 1001;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 640px) {
    top: 12px;
    right: 12px;
    width: 38px;
    height: 38px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const NavBtn = styled(motion.button)`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$side === "left" ? "left: 24px;" : "right: 24px;")}
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: background 0.2s;
  z-index: 1001;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 640px) {
    width: 42px;
    height: 42px;
    ${(p) => (p.$side === "left" ? "left: 8px;" : "right: 8px;")}

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const BottomBar = styled(motion.div)`
  position: fixed;
  bottom: 28px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  z-index: 1001;
`;

const Caption = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.05em;
  max-width: 80vw;
  text-align: center;
`;

const Counter = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.1);
  padding: 7px 16px;
  border-radius: 999px;
  backdrop-filter: blur(8px);
`;

export default function Lightbox({ images, activeIdx, setActiveIdx, onClose }) {
  const isOpen = activeIdx != null;
  const current = isOpen ? images[activeIdx] : null;

  const next = useCallback(() => {
    setActiveIdx((i) => (i == null ? null : (i + 1) % images.length));
  }, [images.length, setActiveIdx]);

  const prev = useCallback(() => {
    setActiveIdx((i) =>
      i == null ? null : (i - 1 + images.length) % images.length
    );
  }, [images.length, setActiveIdx]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, next, prev]);

  return (
    <AnimatePresence>
      {isOpen && current && (
        <Backdrop
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <ImageFrame
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.img
                key={activeIdx}
                src={current.src}
                alt={current.alt || ""}
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              />
            </AnimatePresence>
          </ImageFrame>

          {images.length > 1 && (
            <>
              <NavBtn
                $side="left"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.15 }}
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous image"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </NavBtn>
              <NavBtn
                $side="right"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.15 }}
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next image"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </NavBtn>
            </>
          )}

          <CloseBtn
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.15 }}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </CloseBtn>

          <BottomBar
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.1 }}
          >
            {current.caption && <Caption>{current.caption}</Caption>}
            <Counter>
              {activeIdx + 1} / {images.length}
            </Counter>
          </BottomBar>
        </Backdrop>
      )}
    </AnimatePresence>
  );
}
